import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {
	Typography,
	TextField,
	Select,
	MenuItem,
	Box,
	Button,
	LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import classes from './RevenueForm.module.css';
import {useOutletContext} from 'react-router-dom';
import {
	addRevenueProjectionsApi,
	updateRevenueProjectionsApi,
} from '../Utilities/apis/revenuDataApi';

const Filters = ({
	filterGroup,
	setFilterGroup,
	filterBU,
	setFilterBU,
	uniqueCustomerGroups,
	uniqueBUs,
	handleSubmitAll,
	searchTerm,
	setSearchTerm,
	loader,
}) => {
	const sx = {
		'&::-webkit-scrollbar': {
			width: 4,
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: '#F4F4F4',
		},
		'&::-webkit-scrollbar-thumb': {
			background: 'linear-gradient(155deg, #fce3c7 100%, #f7901d 100%)',
			borderRadius: 2,
		},
	};
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				borderRadius: '0.5rem',
				gap: '2.5rem',
			}}
		>
			<Typography
				style={{
					display: 'flex',
					color: 'black',
					textAlign: 'center',
					fontWeight: 'bold',
					fontSize: '1rem',
				}}
			>
				Revenue Projections
			</Typography>
			<TextField
				label='Search Customer Group'
				variant='outlined'
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				sx={{
					'& .MuiOutlinedInput-root': {
						minWidth: '12rem',
						height: '2rem',
						borderRadius: '4px',
						fontSize: '0.9rem',
						backgroundColor: '#ffffff',
						'&:hover fieldset': {
							borderColor: '#f7901d',
						},
						'&.Mui-focused fieldset': {
							borderColor: '#f7901d',
						},
					},
					'& .MuiInputLabel-root': {
						fontSize: '0.9rem',
						transform: 'translate(0.8rem, 0.5rem) scale(1)',
						textAlign: 'center',
					},
					'& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled':
						{
							transform: 'translate(14px, -6px) scale(0.75)',
							color: 'grey',
						},
				}}
			/>
			<Select
				value={filterGroup}
				onChange={(e) => setFilterGroup(e.target.value)}
				displayEmpty
				style={{
					minWidth: '12rem',
					height: '2rem',
					backgroundColor: '#fff',
					borderRadius: '4px',
					fontSize: '0.9rem',
				}}
				MenuProps={{
					PaperProps: {
						sx: sx,
					},
				}}
			>
				<MenuItem
					value=''
					sx={{
						fontSize: '0.9rem',
					}}
				>
					All Customer Groups
				</MenuItem>
				{uniqueCustomerGroups.map((group, index) => (
					<MenuItem
						key={index}
						value={group}
						sx={{
							fontSize: '0.9rem',
						}}
					>
						{group}
					</MenuItem>
				))}
			</Select>
			<Select
				value={filterBU}
				onChange={(e) => setFilterBU(e.target.value)}
				displayEmpty
				style={{
					minWidth: '12rem',
					height: '2rem',
					backgroundColor: '#fff',
					borderRadius: '4px',
					fontSize: '0.9rem',
				}}
				MenuProps={{
					PaperProps: {
						sx: sx,
					},
				}}
			>
				<MenuItem
					value=''
					sx={{
						fontSize: '0.9rem',
					}}
				>
					All BUs
				</MenuItem>
				{uniqueBUs.map((bu, index) => (
					<MenuItem
						key={index}
						value={bu}
						sx={{
							fontSize: '0.9rem',
						}}
					>
						{bu}
					</MenuItem>
				))}
			</Select>
			<Button
				variant='contained'
				onClick={handleSubmitAll}
				sx={{
					height: '2rem',
					backgroundColor: '#f7901d',
					textTransform: 'none',
					borderRadius: '4px',
					fontSize: '0.9rem',
					'&:hover': {
						backgroundColor: '#e67e10', // Optional: Add hover effect for better UX
					},
				}}
			>
				{loader ? (
					<LinearProgress
						sx={{
							width: '25%', // Adjust width to fit the container
							height: '2px', // Adjust height to make it compact
							backgroundColor: '#f8d7a3', // Optional: Background color for the track
							'& .MuiLinearProgress-bar': {
								backgroundColor: '#f7901d', // Loader color
								height: '0.1rem',
							},
						}}
					/>
				) : (
					'Save'
				)}
			</Button>
		</div>
	);
};

function RevenueTable({data, handleClose, fetchRevenueData, loadRevenueData}) {
	const [futureData, setFutureData] = useState({});
	const [searchTerm, setSearchTerm] = useState('');
	const [filterGroup, setFilterGroup] = useState('');
	const [filterBU, setFilterBU] = useState('');
	const googleIdToken = useOutletContext();
	const [loader, setLoader] = useState(false);

	const getLast12Months = () => {
		const months = [];
		let currentDate = new Date();
		for (let i = 0; i < 12; i++) {
			const month = currentDate.toLocaleString('default', {month: 'short'});
			const year = currentDate.getFullYear();
			months.push(`${month}-${year}`);
			currentDate.setMonth(currentDate.getMonth() - 1);
		}
		return months.reverse();
	};

	const getNext3Months = () => {
		const months = [];
		let nextDate = new Date();
		for (let i = 0; i < 3; i++) {
			nextDate.setMonth(nextDate.getMonth() + 1);
			const month = nextDate.toLocaleString('default', {month: 'short'});
			const year = nextDate.getFullYear();
			months.push(`${month}-${year}`);
		}
		return months;
	};

	const last12Months = getLast12Months();
	const next3Months = getNext3Months();

	const groupData = Object?.keys(data).map((groupKey) => {
		const groupItems = data[groupKey];
		const revenueByMonth = new Array(12).fill(0);
		const futureRevenue = new Array(3).fill(0);
		const futureDates = new Array(3).fill(null);
		const futureIDs = new Array(3).fill(null);
		let BU, CP_L1, CP_L2, Customer_Group;

		groupItems?.forEach((item) => {
			const refDate = new Date(item.Ref_Dt);
			const currentDate = new Date();

			const diffMonths =
				(currentDate.getFullYear() - refDate.getFullYear()) * 12 +
				(currentDate.getMonth() - refDate.getMonth());
			if (diffMonths >= 0 && diffMonths < 12) {
				revenueByMonth[11 - diffMonths] = Math.ceil(item.total_revenue || 0);
			}

			if (diffMonths === -1) {
				futureRevenue[0] = Math.ceil(item.total_revenue || 0);
				futureDates[0] = item.Ref_Dt;
				futureIDs[0] = item.ID;
			}
			if (diffMonths === -2) {
				futureRevenue[1] = Math.ceil(item.total_revenue || 0);
				futureDates[1] = item.Ref_Dt;
				futureIDs[1] = item.ID;
			}
			if (diffMonths === -3) {
				futureRevenue[2] = Math.ceil(item.total_revenue || 0);
				futureDates[2] = item.Ref_Dt;
				futureIDs[2] = item.ID;
			}

			BU = item.BU;
			CP_L1 = item['CP L1'];
			CP_L2 = item['CP L2'];
			Customer_Group = item['Customer Group'];
		});

		return {
			group: groupKey,
			revenueByMonth,
			futureRevenue,
			futureDates,
			futureIDs,
			BU,
			CP_L1,
			CP_L2,
			Customer_Group,
		};
	});

	const filteredData = groupData.filter(
		(group) =>
			group.group.toLowerCase().includes(searchTerm.toLowerCase()) &&
			(!filterGroup || group.Customer_Group === filterGroup) &&
			(!filterBU || group.BU === filterBU)
	);

	const uniqueCustomerGroups = [
		...new Set(groupData.map((group) => group.Customer_Group)),
	];
	const uniqueBUs = [...new Set(groupData.map((group) => group.BU))];

	const handleNextMonthChange = (originalIndex, monthIndex, value) => {
		setFutureData((prev) => {
			const groupData = prev[originalIndex] ? {...prev[originalIndex]} : {};
			groupData[monthIndex] = value;

			return {
				...prev,
				[originalIndex]: groupData,
			};
		});
	};

	const calculateFutureDate = (monthIndex) => {
		const baseDate = new Date();
		baseDate.setMonth(baseDate.getMonth() + monthIndex + 1);
		return baseDate.toISOString().split('T')[0];
	};

	const handleSubmitAll = async () => {
		const promises = [];

		Object.entries(futureData).forEach(([groupIndex, monthData]) => {
			if (!monthData) return;

			const group = groupData[groupIndex];
			if (!group) return;

			Object.entries(monthData).forEach(([monthIndex, projectedValue]) => {
				if (!projectedValue) return;

				const updateID = group.futureIDs[monthIndex];

				if (updateID) {
					promises.push(
						updateRevenueProjectionsApi([
							googleIdToken,
							projectedValue,
							updateID,
						])
					);
				} else {
					const payload = {
						Customer_Group: group.Customer_Group,
						BU: group.BU,
						CP_L1: group.CP_L1,
						CP_L2: group.CP_L2,
						refDate: calculateFutureDate(Number(monthIndex)),
						projectedRevenue: projectedValue,
					};
					promises.push(addRevenueProjectionsApi([googleIdToken, payload]));
				}
			});
		});
		setLoader(true);
		try {
			await Promise.all(promises);
			await fetchRevenueData();
			setFutureData({});
			setLoader(false);
			alert('All data submitted successfully.');
		} catch (error) {
			await fetchRevenueData();
			setFutureData({});
			console.error('Error submitting data:', error);
			setLoader(false);
			alert('Error submitting some data!');
		}
	};

	return (
		<div className={classes.WindowSize}>
			<div className={classes.headerContainer}>
				<div className={classes.leftHeader}>
					<Filters
						filterGroup={filterGroup}
						setFilterGroup={setFilterGroup}
						filterBU={filterBU}
						setFilterBU={setFilterBU}
						uniqueCustomerGroups={uniqueCustomerGroups}
						uniqueBUs={uniqueBUs}
						handleSubmitAll={handleSubmitAll}
						searchTerm={searchTerm}
						setSearchTerm={setSearchTerm}
						loader={loader}
					/>
				</div>
				<div className={classes.rightHeader}>
					<CloseIcon
						onClick={handleClose}
						className={classes.closeButton}
					/>
				</div>
			</div>

			<Box
				className={classes.TableContainer}
				sx={{
					'&::-webkit-scrollbar': {
						width: 6,
					},
					'&::-webkit-scrollbar-track': {
						backgroundColor: '#F4F4F4',
					},
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: '#f7901d',
						borderRadius: 2,
					},
				}}
			>
				{loadRevenueData ? (
					<Box
						style={{
							height: '75vh',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						<LinearProgress
							sx={{
								width: '25%',
								height: '2px',
								backgroundColor: '#f8d7a3',
								'& .MuiLinearProgress-bar': {
									backgroundColor: '#f7901d',
									height: '0.1rem',
								},
							}}
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						/>
					</Box>
				) : (
					<table className={classes.Table}>
						<thead>
							<tr>
								<th>Group</th>
								{last12Months.map((month, index) => (
									<th key={`past-${index}`}>{month}</th>
								))}
								{next3Months.map((month, index) => (
									<th key={`future-${index}`}>{month}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{filteredData.map((group, filteredIndex) => (
								<tr key={`group-${filteredIndex}`}>
									<td style={{textTransform: 'none'}}>{group?.group}</td>
									{group.revenueByMonth.map((revenue, index) => (
										<td key={`revenue-${filteredIndex}-${index}`}>{revenue}</td>
									))}
									{group?.futureRevenue?.map((revenue, monthIndex) => {
										if (!group || !groupData || !Array.isArray(groupData)) {
											console.error('Invalid data:', {group, groupData});
											return null;
										}

										const groupIndex = groupData.findIndex(
											(g) => g?.group === group?.group
										);

										if (groupIndex === -1) {
											console.error('Group not found in groupData:', {
												group,
												groupData,
											});
											return null;
										}

										return (
											<td key={`future-${groupIndex}-${monthIndex}`}>
												<input
													type='text'
													style={{
														width: '30px',
														fontSize: '0.6rem',
														textAlign: 'center',
													}}
													value={
														futureData[groupIndex]?.[monthIndex] ??
														revenue ??
														''
													}
													onChange={(e) =>
														handleNextMonthChange(
															groupIndex,
															monthIndex,
															e.target.value
														)
													}
												/>
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				)}
			</Box>
		</div>
	);
}

const RevenueForm = (props) => {
	const {handleClose, fetchRevenueData, loadRevenueData} = props;
	const RevenueFormData = useSelector((state) => state.Form.RevenueFormData);
	return (
		<RevenueTable
			data={RevenueFormData}
			handleClose={handleClose}
			fetchRevenueData={fetchRevenueData}
			loadRevenueData={loadRevenueData}
		/>
	);
};

export default RevenueForm;
